import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/PageHeading';
import gujaratiFont from '@/style/fonts/noto-sans-gujarati.woff2?url';
import devanagariFont from '@/style/fonts/noto-sans-devanagari.woff2?url';
import type { PrintPage } from '@core/print/printHtml';
import {
  isRoll,
  paperLabel,
  paperSizes,
  rollLengthOf,
  type PaperSize,
} from '@core/print/sheetHtml';

import { CounterBand } from '../bookings/CounterBand';
import { shopSheetPaperOr } from './paper';

/** Points to CSS pixels — the page says its size in points, the preview is drawn in pixels. */
const ptToPx = (pt: number) => (pt * 96) / 72;

/** Wide enough for a desktop scrollbar, so it sits beside the page rather than over its edge. */
const scrollbarRoom = 16;

/**
 * The docket scripts, vendored here as on the website's own pages, so a Gujarati or Hindi line in
 * the sheet prints in its font rather than in tofu. Blocking, for the reason `style.css` gives.
 */
const fontFaces = `<style>
@font-face { font-family: 'Noto Sans Gujarati'; src: url('${gujaratiFont}') format('woff2');
  font-weight: 400 700; font-display: block; }
@font-face { font-family: 'Noto Sans Devanagari'; src: url('${devanagariFont}') format('woff2');
  font-weight: 400 700; font-display: block; }
</style>`;

const withFonts = (html: string) =>
  html.includes('</head>') ? html.replace('</head>', `${fontFaces}</head>`) : fontFaces + html;

function rememberedSheetPaper(
  key: string | undefined,
  fallback: PaperSize,
  allowed: readonly PaperSize[],
): PaperSize {
  if (!key) {
    return fallback;
  }

  try {
    const stored = globalThis.localStorage?.getItem(key);

    return allowed.includes(stored as PaperSize) ? (stored as PaperSize) : fallback;
  } catch {
    // Private browsing throws here; the shop's paper is a fine answer.
    return fallback;
  }
}

/**
 * Printing on the website **the way the phone prints** (owner, 2026-10-22): every document is the
 * core's own page (`@core/print/sheetHtml`, `docketHtml`, `fittingCardHtml`) — the Clean layout the
 * owner chose — on the phone's four papers, starting on the shop's `Print.DefaultPaper`. A roll is
 * the A5 sheet reflowed to its width and cut to the length the preview measures, as on the phone.
 *
 * <p>
 * <strong>The preview is the print.</strong> The page is drawn in a frame of its own at its true
 * size, and Print prints that frame, so the website, the phone's PDF and the paper all come from one
 * HTML page and cannot drift. The page carries no script — everything typed is escaped — so the
 * frame needs none; the roll's length is read from outside, as the phone's measuring line does.
 * </p>
 */
export function SheetPrint({
  eyebrow,
  title,
  build,
  papers = paperSizes,
  remember,
  extra,
  onPrinted,
  onClose,
}: {
  eyebrow: string;
  title: string;
  /** The page for a paper — and, on a roll, the length the preview measured. */
  build: (paper: PaperSize, rollLengthMm: number | undefined) => PrintPage;
  /** What this document offers: all four, or A5 alone for a docket and a fitting card. */
  papers?: readonly PaperSize[];
  /** Remembers this machine's paper for this document, beside the shop's default. */
  remember?: string;
  /** Beside the papers — the docket's language. */
  extra?: React.ReactNode;
  /** Told when Print is pressed — the cash board ticks its day-book step off. */
  onPrinted?: () => void;
  onClose: () => void;
}) {
  const key = remember ? `attirerent.sheet.paper.${remember}` : undefined;
  const [paper, setPaper] = useState<PaperSize>(() =>
    rememberedSheetPaper(
      key,
      shopSheetPaperOr(papers.includes('A5') ? 'A5' : (papers[0] ?? 'A5'), papers),
      papers,
    ),
  );
  // A roll's length as the preview drew it — forgotten when the paper changes.
  const [rollLength, setRollLength] = useState<number | undefined>();
  const frame = useRef<HTMLIFrameElement>(null);
  const page = build(paper, rollLength);
  const roll = isRoll(paper);

  useEffect(() => {
    if (!key) {
      return;
    }

    try {
      globalThis.localStorage?.setItem(key, paper);
    } catch {
      // Not being able to remember the choice is not a reason to fail the print.
    }
  }, [paper, key]);

  /** On a roll, cut the page where its content ends — the phone's `rollLengthScript`, from outside. */
  const measure = () => {
    const body = frame.current?.contentDocument?.body;

    if (!roll || !body) {
      return;
    }

    const mm = rollLengthOf(String((body.scrollHeight * 25.4) / 96));

    if (mm !== null && mm !== rollLength) {
      setRollLength(mm);
    }
  };

  const print = () => {
    const view = frame.current?.contentWindow;

    // The fonts first: a docket printed before its script's font arrives prints boxes.
    void (view?.document.fonts?.ready ?? Promise.resolve()).then(() => view?.print());
    onPrinted?.();
  };

  return (
    <>
      <CounterBand eyebrow={eyebrow} title={title} onBack={onClose} backLabel="Back" />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {papers.length > 1 && (
          <div
            role="group"
            aria-label="Paper"
            className="border-border-color flex overflow-hidden rounded-md border bg-white"
          >
            {papers.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={option === paper}
                onClick={() => {
                  setPaper(option);
                  setRollLength(undefined);
                }}
                className={`cursor-pointer px-3 py-1.5 text-[13px] whitespace-nowrap ${
                  option === paper
                    ? 'bg-primary font-semibold text-white'
                    : 'hover:bg-light text-gray-900'
                }`}
              >
                {paperLabel[option]}
              </button>
            ))}
          </div>
        )}

        {extra}

        <Button
          variant="primary"
          icon="icon icon-printer"
          shortcut="print"
          onClick={print}
          className="ms-auto"
        >
          Print
        </Button>
      </div>

      <div className="bg-light border-border-color overflow-auto rounded-md border p-4">
        <iframe
          ref={frame}
          title={`${title} on ${paperLabel[paper]}`}
          srcDoc={withFonts(page.html)}
          onLoad={measure}
          className="mx-auto block border-0 bg-white shadow-sm"
          // Room for the frame's own scrollbar beside the page: a document longer than one page (a day
          // book, a long roll) scrolls, and a scrollbar drawn over the page hid a roll's amounts.
          style={{ width: ptToPx(page.widthPt) + scrollbarRoom, height: ptToPx(page.heightPt) }}
        />
      </div>
    </>
  );
}
